import React from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';
import Heading from '@cotyorg/ccx-atoms/Heading';
import Paragraph from '@cotyorg/ccx-atoms/Paragraph';
import Link from '@cotyorg/ccx-atoms/Link';
import ProductSwatchColours from '@cotyorg/ccx-organism-product-swatch/components/ProductSwatchColours';
import ProductDetailsAccordion from '@cotyorg/ccx-molecules/ProductDetails/components/ProductDetailsAccordion';
import { trackSelectedShadeEvent } from '@cotyorg/ccx-utils/js/tracking/adobe-dtm/events/product';
import OptionalSection from '@cotyorg/ccx-utils/js/components/OptionalSection';
import ProductRetailers from '../ProductRetailers';
import { isSmallBreakpoint, showBV } from '../../utils';
import { updateQueryStringWithoutReload } from './helpers';
import ChannelAdvisorWidget from '../../molecules/ChannelAdvisorWidget/ChannelAdvisorWidget';

const ProductInfo = ({
  sectionHeadings,
  productDetails,
  currentBreakpoint,
  categoryTitle,
  subcategoryTitle,
  productSwatch,
  locale,
  hidePrice,
  selectedSKU,
}) => {
  const isChannelAdvisorVisible = Boolean(
    R.path(['channelAdvisorDetails', 'channel_advisor_url'], productDetails) &&
      R.path(['channelAdvisorDetails', 'country_pid'], productDetails)
  );

  return (
    <div className="product-info flex-container flex-dir-column">
      <div className="product-info__inner">
        {!isSmallBreakpoint({ currentBreakpoint }) && (
          <Heading
            extraClass="h2 product-info__heading"
            level={1}
            data-test="product-heading"
          >
            {R.path(['productDetails', 'heading'], sectionHeadings)}
          </Heading>
        )}

        {R.prop('descriptionHTML', productDetails) ? (
          <div
            className="product-info__description"
            data-test="product-description"
            dangerouslySetInnerHTML={{
              __html: R.prop('descriptionHTML', productDetails),
            }}
          />
        ) : (
          <Paragraph
            extraClass="product-info__description"
            testAttribute="product-description"
          >
            {R.prop('description', productDetails)}
          </Paragraph>
        )}

        <OptionalSection
          renderCondition={showBV(locale)}
          section={() => (
            <div className="product-info__rating flex-container align-center">
              <div
                data-bv-show="inline_rating"
                data-bv-seo="false"
                data-bv-product-id={R.prop('bvid', productDetails)}
              />

              {
                R.prop('numberOfReviews', productDetails) > 0 ? (
                  /* eslint-disable jsx-a11y/anchor-is-valid */
                  <Link anchor="#reviews">
                    {R.prop('readReviewsLabel', productDetails)}
                  </Link>
                ) : (
                  <Link anchor="#reviews">
                    {R.prop('noReviewsLabel', productDetails)}
                  </Link>
                )
                /* eslint-enable jsx-a11y/anchor-is-valid */
              }
            </div>
          )}
        />
      </div>

      <div className="product-info__swatches">
        <ProductSwatchColours
          label={R.prop('shadeSelectLabel', productDetails)}
          separator="/ "
          labelFirst
          nameFirst
          onChange={({ shade, sku }) => {
            updateQueryStringWithoutReload({ shade });

            trackSelectedShadeEvent({
              shade: shade.toLowerCase(),
              sku,
              name: R.compose(R.toLower(), R.prop('title'))(productDetails),
              type: subcategoryTitle.toLowerCase(),
            });
          }}
        />
      </div>

      <ProductDetailsAccordion
        minusIconSrc="/ccx-files/assets/chevron-up.svg"
        plusIconSrc="/ccx-files/assets/chevron-down.svg"
        iconWidth={16}
      />

      {R.prop('RRPTitle', productDetails) && R.prop('RRP', productDetails) && (
        <p className="product-info__rrp">
          <strong>{R.prop('RRPTitle', productDetails)}</strong>:{' '}
          {R.prop('RRP', productDetails)}
        </p>
      )}

      {isChannelAdvisorVisible && (
        <div className="channel-advisor-wrapper">
          <ChannelAdvisorWidget
            selectedSKU={selectedSKU}
            url={R.path(
              ['channelAdvisorDetails', 'channel_advisor_url'],
              productDetails
            )}
            caPid={R.path(
              ['channelAdvisorDetails', 'country_pid'],
              productDetails
            )}
          />
        </div>
      )}

      <ProductRetailers
        sectionHeadings={sectionHeadings}
        productTitle={R.prop('title', productDetails)}
        categoryTitle={categoryTitle}
        subcategoryTitle={subcategoryTitle}
        productSwatch={productSwatch}
        locale={locale}
        hidePrice={hidePrice}
      />
    </div>
  );
};

ProductInfo.propTypes = {
  productDetails: PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    tabs: PropTypes.array,
    numberOfReviews: PropTypes.number,
  }),
  sectionHeadings: PropTypes.shape({
    relatedProducts: PropTypes.shape({
      heading: PropTypes.string,
    }),
    howToSteps: PropTypes.shape({
      heading: PropTypes.string,
    }),
    retailers: PropTypes.shape({
      heading: PropTypes.string,
    }),
  }),
  currentBreakpoint: PropTypes.string.isRequired,
  categoryTitle: PropTypes.string,
  subcategoryTitle: PropTypes.string,
  productSwatch: PropTypes.shape({
    swatches: PropTypes.arrayOf(
      PropTypes.shape({
        sku: PropTypes.string,
        itemName: PropTypes.string,
        skuColor: PropTypes.string,
      })
    ),
  }),
  locale: PropTypes.string,
  hidePrice: PropTypes.bool,
  selectedSKU: PropTypes.string,
};

export default ProductInfo;
